import React from 'react';
import injectSheet from 'react-jss';
import classNames from 'classnames';
import styleSheet from './styles';

interface Props {
  // classes, placeholder, onChange, value, name, onBlur
  error?: boolean;
  max?: string;
  min?: string;
  maxLength?: number;
  label?: string;
  type: string;
  name: string;
  value?: string;
  classes?: any;
  placeholder?: string;
  textInput?: string;
  limitCharacterText?: string;
  disabled?: boolean;
  // Used for styles
  // eslint-disable-next-line react/no-unused-prop-types
  overrideStyles?: {
    display?: string;
    margin?: string;
    padding?: string;
    height?: string;
    width?: string;
    border?: string;
    borderRadius?: string;
    fontSize?: string;
    color?: string;
    fontWeight?: string;
    lineHeight?: string;
  };
  onChange?: (e: object) => void;
  onKeyUp?: (e: object) => void;
  onFocus?: (e: React.FocusEvent<any>) => void;
  onBlur?: (e: React.FocusEvent<any>) => void;
  domRef?: any;
  ariaLabel?: string;
}

const Input: React.FC<Props> = (props: Props) => {
  const {
    error,
    max,
    min,
    maxLength,
    label,
    type,
    name,
    value,
    classes,
    placeholder,
    textInput,
    limitCharacterText,
    disabled,
    onChange,
    onKeyUp,
    onFocus,
    onBlur,
    domRef,
    ariaLabel,
  } = props;

  const validateInput = (inputValue: string) => {
    let validatedInput = inputValue;
    const allowedChars = /[^a-z0-9~`!@#$%^&*()\-_+={}[\]|\\/:;"'<>,.?\s]/gi;
    if (allowedChars.test(validatedInput)) {
      validatedInput = validatedInput.replace(allowedChars, '');
    }
    return validatedInput;
  };

  const validateOnChange = (event: React.ChangeEvent<any>) => {
    const validatedEvent = { ...event };
    const validatedInput = validateInput(event.target.value);

    if (validatedInput !== event.target.value) {
      const oldLength = event.target.value.length;
      const oldIndex = event.target.selectionStart;
      const newIndex = Math.max(
        0,
        validatedInput.length - oldLength + oldIndex
      );
      validatedEvent.target.value = validatedInput;
      validatedEvent.target.selectionStart = newIndex;
      validatedEvent.target.selectionEnd = newIndex;
    }

    const response = {
      value: validatedEvent.target.value,
      name: validatedEvent.target.name,
      fieldId: validatedEvent.target.name,
    };

    onChange({ target: response });
  };

  return (
    <>
      {label && (
        <label
          className={classNames(classes.label, {
            [classes.hideOnMobile]: label.length === 1,
          })}
          htmlFor={name}
          data-testid="label"
        >
          {label}
        </label>
      )}
      <input
        style={{
          textTransform: type === 'date' ? 'uppercase' : 'inherit',
        }}
        className={classNames(classes.input, {
          [classes.error]: error,
        })}
        placeholder={placeholder}
        type={type}
        name={name}
        value={value}
        onChange={(e) => validateOnChange(e)}
        onKeyUp={onKeyUp}
        onFocus={onFocus}
        onBlur={onBlur}
        max={max}
        maxLength={maxLength}
        min={min}
        autoComplete="off"
        disabled={disabled || false}
        ref={domRef}
        aria-label={ariaLabel}
        data-testid="input"
      />
      {textInput && textInput.length >= 100 && limitCharacterText && (
        <span
          className={classes.limitCharacterText}
          data-testid="limitCharacterText"
        >
          {limitCharacterText}
        </span>
      )}
    </>
  );
};

Input.defaultProps = {
  error: false,
  max: undefined,
  min: undefined,
  maxLength: undefined,
  label: undefined,
  value: undefined,
  classes: {},
  placeholder: undefined,
  textInput: undefined,
  limitCharacterText: undefined,
  disabled: false,
  overrideStyles: {},
  onChange: () => {},
  onKeyUp: () => {},
  onFocus: () => {},
  onBlur: () => {},
  domRef: undefined,
  ariaLabel: undefined,
};

export default injectSheet(styleSheet)(Input);
